
import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Clock, 
  Truck, 
  DollarSign,
  Copy,
  ArrowUpDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

const Services = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredField, setHoveredField] = useState(null);

  // Copy function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Đã sao chép vào clipboard!");
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert("Không thể sao chép");
    }
  };

  const [services] = useState([
    {
      id: 'SV001',
      name: 'Hỏa tốc Hà Nội - Đà Nẵng',
      route: 'Hà Nội - Đà Nẵng',
      type: 'Hỏa tốc',
      deliveryTime: '4 giờ',
      pricingType: 'fixed',
      basePrice: 50000,
      weightPrice: 0,
      additionalServices: ['Gói hàng', 'Thu COD'],
      status: 'active',
      createdAt: '2025-01-10T08:00:00+07:00'
    },
    {
      id: 'SV002',
      name: 'Nhanh Hà Nội - HCM',
      route: 'Hà Nội - Hồ Chí Minh',
      type: 'Nhanh',
      deliveryTime: '24 giờ',
      pricingType: 'weight',
      basePrice: 20000,
      weightPrice: 15000,
      additionalServices: ['Thu COD', 'Bảo hiểm'],
      status: 'active',
      createdAt: '2025-01-08T10:30:00+07:00'
    },
    {
      id: 'SV003',
      name: 'Tiết kiệm Nội thành Hà Nội',
      route: 'Nội thành Hà Nội',
      type: 'Tiết kiệm',
      deliveryTime: '48-72 giờ',
      pricingType: 'weight',
      basePrice: 15000,
      weightPrice: 8000,
      additionalServices: ['Thu COD'],
      status: 'inactive',
      createdAt: '2025-01-05T14:15:00+07:00'
    }
  ]);

  const [routes] = useState([
    'Hà Nội - Đà Nẵng',
    'Hà Nội - Hồ Chí Minh',
    'Đà Nẵng - Hồ Chí Minh',
    'Nội thành Hà Nội',
    'Nội thành Đà Nẵng',
    'Nội thành Hồ Chí Minh'
  ]);

  const [additionalServices] = useState([
    'Gói hàng',
    'Thu COD',
    'Bảo hiểm',
    'Giao trong ngày',
    'Giao cuối tuần',
    'Xác nhận trước khi giao'
  ]);

  const [formData, setFormData] = useState({
    name: '',
    route: '',
    type: 'Nhanh',
    deliveryTime: '',
    pricingType: 'fixed',
    basePrice: 0,
    weightPrice: 0,
    description: '',
    additionalServices: [] as string[],
    status: 'active'
  });

  const getStatusColor = (status: string) => {
    return status === 'active'
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Hỏa tốc': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Nhanh': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Tiết kiệm': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.route.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdditionalServiceChange = (service: string, checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        additionalServices: [...formData.additionalServices, service]
      });
    } else {
      setFormData({
        ...formData,
        additionalServices: formData.additionalServices.filter(s => s !== service)
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Quản lý dịch vụ</h1>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Thêm dịch vụ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Thêm dịch vụ mới</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tên dịch vụ</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ví dụ: Hỏa tốc Hà Nội - Đà Nẵng"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tuyến đường</Label>
                  <Select value={formData.route} onValueChange={(value) => setFormData({...formData, route: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn tuyến đường" />
                    </SelectTrigger>
                    <SelectContent>
                      {routes.map(route => (
                        <SelectItem key={route} value={route}>
                          {route}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hình thức giao hàng</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hỏa tốc">Hỏa tốc</SelectItem>
                      <SelectItem value="Nhanh">Nhanh</SelectItem>
                      <SelectItem value="Tiết kiệm">Tiết kiệm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Thời gian giao hàng</Label>
                  <Input
                    value={formData.deliveryTime}
                    onChange={(e) => setFormData({...formData, deliveryTime: e.target.value})}
                    placeholder="Ví dụ: 4 giờ, 24 giờ, 48-72 giờ"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <Label>Cơ chế tính phí</Label>
                <Select value={formData.pricingType} onValueChange={(value) => setFormData({...formData, pricingType: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Cố định theo đơn</SelectItem>
                    <SelectItem value="weight">Theo cân nặng</SelectItem>
                  </SelectContent>
                </Select>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      {formData.pricingType === 'fixed' ? 'Giá cố định (VNĐ/đơn)' : 'Giá cơ bản (VNĐ)'}
                    </Label>
                    <Input
                      type="number"
                      value={formData.basePrice}
                      onChange={(e) => setFormData({...formData, basePrice: Number(e.target.value)})}
                      placeholder="0"
                    />
                  </div>
                  {formData.pricingType === 'weight' && (
                    <div className="space-y-2">
                      <Label>Giá theo cân nặng (VNĐ/kg)</Label>
                      <Input
                        type="number"
                        value={formData.weightPrice}
                        onChange={(e) => setFormData({...formData, weightPrice: Number(e.target.value)})}
                        placeholder="0"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Mô tả dịch vụ</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Mô tả chi tiết về dịch vụ..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Dịch vụ kèm theo</Label>
                <div className="grid grid-cols-2 gap-2">
                  {additionalServices.map(service => (
                    <div key={service} className="flex items-center space-x-2">
                      <Checkbox
                        id={service}
                        checked={formData.additionalServices.includes(service)}
                        onCheckedChange={(checked) => handleAdditionalServiceChange(service, checked as boolean)}
                      />
                      <Label htmlFor={service} className="text-sm">{service}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Trạng thái</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Kích hoạt</SelectItem>
                    <SelectItem value="inactive">Tạm dừng</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Hủy
                </Button>
                <Button onClick={() => setShowCreateDialog(false)}>
                  Tạo dịch vụ
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Danh sách dịch vụ</CardTitle>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Tìm kiếm theo tên hoặc tuyến đường..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Tên dịch vụ
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">Tuyến đường</TableHead>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Hình thức
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">Thời gian</TableHead>
                  <TableHead className="min-w-[16em]">Giá cước</TableHead>
                  <TableHead className="min-w-[16em]">Dịch vụ kèm</TableHead>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Trạng thái
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="w-[200px] text-center sticky right-0 bg-background z-10 border-l">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredServices.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell 
                      className="font-medium cursor-pointer flex items-center gap-2"
                      onMouseEnter={() => setHoveredField(`name-${service.id}`)}
                      onMouseLeave={() => setHoveredField(null)}
                      onClick={() => copyToClipboard(service.name)}
                    >
                      {service.name}
                      {hoveredField === `name-${service.id}` && (
                        <Copy className="h-3 w-3 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div 
                        className="flex items-center gap-2 cursor-pointer"
                        onMouseEnter={() => setHoveredField(`route-${service.id}`)}
                        onMouseLeave={() => setHoveredField(null)}
                        onClick={() => copyToClipboard(service.route)}
                      >
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <span>{service.route}</span>
                        {hoveredField === `route-${service.id}` && (
                          <Copy className="h-3 w-3 text-muted-foreground ml-1" />
                        )}
                      </div>
                    </TableCell>
                  <TableCell>
                    <Badge className={getTypeColor(service.type)}>
                      {service.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{service.deliveryTime}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{formatPrice(service.basePrice)}</div>
                        {service.pricingType === 'weight' && service.weightPrice > 0 && (
                          <div className="text-xs text-muted-foreground">
                            +{formatPrice(service.weightPrice)}/kg
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {service.additionalServices.slice(0, 2).map(s => (
                        <Badge key={s} variant="outline" className="text-xs">
                          {s}
                        </Badge>
                      ))}
                      {service.additionalServices.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{service.additionalServices.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(service.status)}>
                      {service.status === 'active' ? 'Kích hoạt' : 'Tạm dừng'}
                    </Badge>
                  </TableCell>
                  <TableCell className="sticky right-0 bg-background z-10 border-l">
                    <div className="flex items-center gap-1 justify-center">
                      <Button size="sm" variant="outline" title="Chỉnh sửa">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive" title="Xóa">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Services;
